import { createContext, useCallback, useContext, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import { api, guardarToken, limparToken, lerToken } from '../lib/api.ts';

export type Nivel = 'visualizar' | 'editar' | 'excluir';

/** Cumulativo: quem exclui também edita, quem edita também visualiza. */
const ORDEM: Record<Nivel, number> = { visualizar: 1, editar: 2, excluir: 3 };

export interface Usuario {
  id: string;
  nome: string;
  email: string;
  /** Código do perfil. Deixou de ser lista fixa: perfil é cadastro. */
  perfil: string;
}

export type Permissoes = Record<string, Nivel>;

interface Contexto {
  usuario: Usuario | null;
  carregando: boolean;
  entrar: (email: string, senha: string) => Promise<void>;
  sair: () => void;
  /** O que este usuário pode, por módulo. Vem do servidor a cada sessão. */
  permissoes: Permissoes;
  pode: (modulo: string, nivel: Nivel) => boolean;
  ehAdmin: boolean;
  podeFechar: boolean;
  podeAdministrarRegras: boolean;
}

const AuthContext = createContext<Contexto | null>(null);

interface RespostaSessao {
  usuario: Usuario;
  permissoes: Permissoes;
}

export function ProvedorAuth({ children }: { children: ReactNode }) {
  const [usuario, setUsuario] = useState<Usuario | null>(null);
  const [permissoes, setPermissoes] = useState<Permissoes>({});
  const [carregando, setCarregando] = useState(true);

  // Um token guardado no navegador pode ter expirado. Em vez de confiar nele,
  // pergunta ao servidor quem somos — é a única resposta que vale. As
  // permissões vêm na mesma resposta, e não de dentro do token, para uma
  // mudança na matriz valer sem obrigar a sair e entrar de novo.
  useEffect(() => {
    if (lerToken() === null) {
      setCarregando(false);
      return;
    }
    api<RespostaSessao>('/auth/eu')
      .then((r) => {
        setUsuario(r.usuario);
        setPermissoes(r.permissoes ?? {});
      })
      .catch(() => {
        limparToken();
        setUsuario(null);
        setPermissoes({});
      })
      .finally(() => setCarregando(false));
  }, []);

  const entrar = useCallback(async (email: string, senha: string) => {
    const r = await api<RespostaSessao & { token: string }>('/auth/login', {
      metodo: 'POST',
      corpo: { email, senha },
    });
    guardarToken(r.token);
    setUsuario(r.usuario);
    setPermissoes(r.permissoes ?? {});
  }, []);

  const sair = useCallback(() => {
    // Avisa o servidor antes de esquecer o token, para a sessão fechar com o
    // motivo certo. Se a chamada falhar, sair local acontece do mesmo jeito:
    // ninguém deve ficar preso dentro do sistema porque a rede caiu.
    api('/auth/sair', { metodo: 'POST' }).catch(() => undefined);
    limparToken();
    setUsuario(null);
    setPermissoes({});
  }, []);

  // Esconder um botão é conveniência, não segurança: quem manda é o servidor,
  // que confere a permissão em cada rota.
  const pode = useCallback(
    (modulo: string, nivel: Nivel): boolean => {
      const concedido = permissoes[modulo];
      return concedido !== undefined && ORDEM[concedido] >= ORDEM[nivel];
    },
    [permissoes],
  );

  const valor: Contexto = {
    usuario,
    carregando,
    entrar,
    sair,
    permissoes,
    pode,
    ehAdmin: usuario?.perfil === 'admin',
    podeFechar: pode('fechamento', 'editar'),
    podeAdministrarRegras: pode('regras', 'editar'),
  };

  return <AuthContext.Provider value={valor}>{children}</AuthContext.Provider>;
}

export function useAuth(): Contexto {
  const ctx = useContext(AuthContext);
  if (ctx === null) throw new Error('useAuth precisa estar dentro de ProvedorAuth');
  return ctx;
}
